import type {
  TemplateFieldLike,
  OnChangeRule,
  VisibleWhenCondition,
} from "./opportunityFormTypes";

export type { PreviewValues } from "./opportunityFormTypes";

export const normalizeOptions = (
  options: Array<string | { label: string; value: string | number }> = [],
): Array<{ label: string; value: string | number }> =>
  options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

export const setByPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void => {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let cur = obj;
  for (let index = 0; index < parts.length; index++) {
    const part = parts[index];
    if (index === parts.length - 1) {
      cur[part] = value;
    } else {
      if (cur[part] === undefined || cur[part] === null) {
        const next = parts[index + 1];
        cur[part] = /^\d+$/.test(next) ? [] : {};
      }
      cur = cur[part] as Record<string, unknown>;
    }
  }
};

export const getByPath = <T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  path: string,
): T | undefined => {
  if (!obj) return undefined;
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur == null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur as T | undefined;
};

export const getMinDateString = (minDaysFromNow?: number): string | undefined => {
  if (minDaysFromNow === undefined) return undefined;
  const date = new Date();
  date.setDate(date.getDate() + minDaysFromNow);
  return date.toISOString().split("T")[0];
};

// New repeatable row, pre-filled with the "default" values from the template.
export const buildEmptyRow = (
  fields: TemplateFieldLike[],
): Record<string, unknown> => {
  const row: Record<string, unknown> = {};

  fields.forEach((field) => {
    if (field.default !== undefined) {
      row[field.key] = field.default;
    }
  });

  return row;
};

export const isGroupLike = (field: TemplateFieldLike): boolean =>
  !!(field.fields && Array.isArray(field.fields) && field.fields.length > 0);


export const LabelRow = ({
  label,
  required,
}: {
  label: string;
  required?: boolean;
  type?: string;
}) => (
  <div className="flex flex-col mb-1.5">
    <label className="text-xs font-medium text-[#71717A] leading-tight">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  </div>
);

export const writePath = (stepId: string, key: string) =>
  stepId ? `${stepId}.${key}` : key;

export const COMMON_ROOT_SCOPES = ["core", "details", "funding", "policies", "notes"];

export const resolveByKey = (
  formData: Record<string, unknown>,
  key: string,
  parentPath?: string,
): unknown => {
  if (!formData || !key) return undefined;

  let resolved = parentPath
    ? getByPath(formData, `${parentPath}.${key}`)
    : undefined;
  if (resolved !== undefined) return resolved;

  resolved = getByPath(formData, key);
  if (resolved !== undefined) return resolved;

  for (const scope of COMMON_ROOT_SCOPES) {
    resolved = getByPath(formData, `${scope}.${key}`);
    if (resolved !== undefined) return resolved;
  }

  const camel = key.replace(/[_-](\w)/g, (_, ch) => ch.toUpperCase());
  const snake = key.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);

  if (camel !== key) {
    resolved = parentPath
      ? getByPath(formData, `${parentPath}.${camel}`)
      : undefined;
    if (resolved !== undefined) return resolved;

    resolved = getByPath(formData, camel);
    if (resolved !== undefined) return resolved;

    for (const scope of COMMON_ROOT_SCOPES) {
      resolved = getByPath(formData, `${scope}.${camel}`);
      if (resolved !== undefined) return resolved;
    }
  }

  if (snake !== key) {
    resolved = parentPath
      ? getByPath(formData, `${parentPath}.${snake}`)
      : undefined;
    if (resolved !== undefined) return resolved;

    resolved = getByPath(formData, snake);
    if (resolved !== undefined) return resolved;

    for (const scope of COMMON_ROOT_SCOPES) {
      resolved = getByPath(formData, `${scope}.${snake}`);
      if (resolved !== undefined) return resolved;
    }
  }

  return undefined;
};

export const getFormVal = (
  formData: Record<string, unknown>,
  stepOrPath: string,
  keyMaybe?: string,
): unknown => {
  if (keyMaybe !== undefined) {
    const path = writePath(stepOrPath, keyMaybe);
    let resolved = getByPath(formData, path);
    if (resolved !== undefined) return resolved;

    resolved = getByPath(formData, keyMaybe);
    if (resolved !== undefined) return resolved;

    for (const scope of ["details", "core", "funding", "policies"]) {
      const scopedValue = getByPath(formData, `${scope}.${keyMaybe}`);
      if (scopedValue !== undefined) return scopedValue;
    }

    const camel = keyMaybe.replace(/[_-](\w)/g, (_, ch) => ch.toUpperCase());
    const snake = keyMaybe.replace(
      /[A-Z]/g,
      (match) => `_${match.toLowerCase()}`,
    );

    if (camel !== keyMaybe) {
      resolved =
        getByPath(formData, writePath(stepOrPath, camel)) ??
        getByPath(formData, camel);
      if (resolved !== undefined) return resolved;
    }

    if (snake !== keyMaybe) {
      resolved =
        getByPath(formData, writePath(stepOrPath, snake)) ??
        getByPath(formData, snake);
      if (resolved !== undefined) return resolved;
    }

    return undefined;
  }

  return getByPath(formData, stepOrPath);
};

export const evalVisible = (
  visibleWhen: Record<string, unknown>,
  formData: Record<string, unknown>,
  parentPath?: string,
): boolean => {
  if (!visibleWhen) return true;

  const check = (entry: Record<string, unknown>): boolean => {
    if (typeof entry !== "object" || Array.isArray(entry)) return false;

    for (const [fieldKey, expectedValue] of Object.entries(entry)) {
      const actual = resolveByKey(formData, fieldKey, parentPath);
      if (Array.isArray(expectedValue)) {
        if (!expectedValue.includes(actual)) return false;
      } else {
        if (actual !== expectedValue) return false;
      }
    }
    return true;
  };

  const { anyOf, allOf } = visibleWhen;
  if (anyOf) return (anyOf as VisibleWhenCondition[]).some(check);
  if (allOf) return (allOf as VisibleWhenCondition[]).every(check);
  return check(visibleWhen);
};

export const filterOptions = (
  opts: Array<
    | string
    | {
        label: string;
        value: string | number;
        visibleWhen?: VisibleWhenCondition;
      }
  >,
  contextData: Record<string, unknown>,
): Array<{
  label: string;
  value: string | number;
  visibleWhen?: VisibleWhenCondition;
}> => {
  return opts
    .map((opt) => (typeof opt === "string" ? { label: opt, value: opt } : opt))
    .filter((option) => {
      if (!option.visibleWhen) return true;
      return evalVisible(option.visibleWhen, contextData);
    });
};

export const resolveMaxWhen = (
  maxWhen: Record<string, unknown>,
  contextData: Record<string, unknown>,
): number | undefined => {
  if (!maxWhen || !contextData) return undefined;
  const { max: conditionalMax, ...conditions } = maxWhen;
  const allMatch = Object.entries(conditions).every(
    ([fieldKey, expectedValue]) =>
      resolveByKey(contextData, fieldKey) === expectedValue,
  );
  return allMatch ? Number(conditionalMax) : undefined;
};

export const validateField = (
  field: TemplateFieldLike,
  value: unknown,
  contextData?: Record<string, unknown>,
): string | null => {
  const validation = field.validation || {};
  const empty =
    value === undefined ||
    value === null ||
    (typeof value === "string" && !value.trim());

  if (validation.required) {
    if (field.type === "multiselect") {
      if (!Array.isArray(value) || value.length === 0) {
        return `${field.label || field.key} is required`;
      }
    } else if (empty) {
      return `${field.label || field.key} is required`;
    }
  }

  if (!empty) {
    if (field.type === "number" || !isNaN(Number(value))) {
      const numericValue = Number(value);
      if (validation.min !== undefined && numericValue < validation.min)
        return `${field.label || field.key} must be ≥ ${validation.min}`;
      if (validation.max !== undefined && numericValue > validation.max)
        return `${field.label || field.key} must be ≤ ${validation.max}`;
      if (validation.maxWhen && contextData) {
        const condMax = resolveMaxWhen(validation.maxWhen, contextData);
        if (condMax !== undefined && numericValue > condMax)
          return `${field.label || field.key} must be ≤ ${condMax}`;
      }
    } else {
      if (
        validation.minLength !== undefined &&
        String(value).length < validation.minLength
      )
        return `${field.label || field.key} must be at least ${validation.minLength} chars`;
      if (
        validation.maxLength !== undefined &&
        String(value).length > validation.maxLength
      )
        return `${field.label || field.key} must be at most ${validation.maxLength} chars`;
      if (validation.pattern) {
        try {
          if (!new RegExp(validation.pattern).test(String(value)))
            return `${field.label || field.key} format is invalid`;
        } catch {
          // ignore invalid regex pattern
        }
      }
    }
  }

  return null;
};

export const slugify = (text?: string): string => {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const applyOnChangeRules = (
  rules: OnChangeRule[] = [],
  path: string,
  updateForm: (path: string, val: unknown) => void,
): void => {
  for (const rule of rules) {
    if (rule.action === "reset") {
      const parts = path.replace(/\[(\d+)\]/g, "[$1]").split(".");
      parts[parts.length - 1] = rule.target;
      updateForm(parts.join("."), undefined);
    }
  }
};
